/**
 * Day.js configuration with required plugins for Ant Design compatibility
 *
 * IMPORTANT: Always import dayjs from this file, NOT directly from 'dayjs' package.
 * This ensures all necessary plugins are loaded to prevent runtime errors.
 *
 * ESLint will throw an error if you import from 'dayjs' directly.
 *
 * Usage:
 *   import dayjs from '@/utils/dayjs-config';
 *   import { Dayjs } from '@/utils/dayjs-config';
 */

import dayjs from 'dayjs';

// Import required plugins
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';

// Import locales
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';

// Extend dayjs with all required plugins
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

// Set default timezone
dayjs.tz.setDefault('Asia/Hong_Kong');

// Set default locale
dayjs.locale('en');

export default dayjs;

// Type exports for compatibility
export type { Dayjs } from 'dayjs';
